import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { selectorCoreLogin } from '@bepatient/core';
import { selectorMyAccountSelectedSettingType } from '@bepatient/my-account-store';
import { selectorOnboardingOnboarding } from '@bepatient/onboarding-store';
import { select, Store } from '@ngrx/store';
import { take, takeWhile } from 'rxjs/operators';

enum RuleTypes {
  MINIMAL_LENGTH,
  ALPHABETICAL_CHAR,
  SPECIAL_CHAR,
  NUMERIC_CHAR,
  PASSWORDS_IDENTICAL
}

interface Rule {
  regex?: RegExp;
  message: string;
  validated?: boolean;
}

interface Strength {
  color: string;
  label: string;
  value: number;
}

export interface NewAndCurrentPasswordsObject {
  iAuth?: string; // iAuth is required to reset forgotten password
  newPassword: string;
  currentPassword: string;
  confirmPassword?: string;
}

@Component({
  selector: 'bepatient-user-password',
  templateUrl: './user-password.component.html',
  styleUrls: ['./user-password.component.scss'],
  standalone: false
})
export class UserPasswordComponent implements OnInit, AfterViewInit, OnDestroy {
  private store = inject<Store<any>>(Store);

  @Input() color: string;
  @Input() title: string;
  @Input() subtitle: string;
  @Input() buttonLabel: string;
  @Input() passwordLabel: string;
  @Input() confirmationLabel: string;
  @Input() currentPasswordLabel: string;
  @Input() requireCurrentPassword = false;
  @Input() errorLabel: string;
  @Input() hasError = false;
  @Input() passwordResetPolicy: Rule[];

  @Output() password = new EventEmitter<NewAndCurrentPasswordsObject>();

  iAuth: string;
  passwordPolicy: Rule[];
  valid = false;
  passwordForm: UntypedFormGroup;
  passwordFormInfo: any;

  passwordStrength: Strength;
  progressBarDefaultBackground = 'danger';
  progressCount: number;
  showPasswordStrength: boolean;
  progressingColor: string;
  passwordStrengthLabel: string;
  passwordsIdentical: Rule;

  private alive = true;

  get formIsValid(): boolean {
    return this.passwordForm.get('passwordFormValid').value;
  }

  get currentPasswordFieldIsValid(): boolean {
    return this.passwordForm.get('currentPassword').status === 'VALID';
  }

  constructor() {
    this.passwordForm = new UntypedFormGroup({
      currentPassword: new UntypedFormControl('', Validators.required),
      password: new UntypedFormControl(''),
      confirmation: new UntypedFormControl(''),
      passwordFormValid: new UntypedFormControl(false, Validators.requiredTrue)
    });

    // Assigning language translation for static variables
    this.passwordStrengthLabel = 'USER_PASSWORD.PASSWORD_STRENGTH.LABEL';
    this.passwordsIdentical = {
      message: 'USER_PASSWORD.PASSWORDS_IDENTICAL'
    };
    this.passwordStrength = {
      color: '#FF8383',
      label: '',
      value: 0
    };
  }

  ngOnInit(): void {
    this.store
      .pipe(select(selectorCoreLogin), take(1))
      .subscribe(login => (this.iAuth = login.iAuth));
    if (this.requireCurrentPassword) {
      this.store
        .pipe(select(selectorMyAccountSelectedSettingType), take(1))
        .subscribe(v => {
          if (v) {
            this.passwordFormInfo = v.find(form => !!form.passwordPolicy);
            this.passwordPolicy = this.passwordFormInfo
              ? this.passwordFormInfo.passwordPolicy
              : void 0;
          }
        });
    } else {
      this.passwordResetPolicy
        ? (this.passwordPolicy = this.passwordResetPolicy)
        : this.store
            .pipe(select(selectorOnboardingOnboarding), take(1))
            .subscribe(v => {
              if (v.steps) {
                v.steps.map(step => {
                  if (step['passwordPolicy']) {
                    this.passwordPolicy = step['passwordPolicy'];
                  }
                });
              }
            });
    }
  }

  ngAfterViewInit(): void {
    if (this.passwordForm.value.password.length === 0) {
      this.resetValidatedRule();
    }
    this.passwordForm.valueChanges
      .pipe(takeWhile(() => this.alive))
      .subscribe(values => {
        this.passwordPolicy.forEach(rule => {
          rule.regex = new RegExp(rule.regex, 'g');
          rule.validated = rule.regex.test(values.password);
        });

        // Identical passwords
        this.passwordsIdentical.validated =
          values.password.length && values.password === values.confirmation;

        // conditional display/hide of password strength bar based on password input change
        this.showPasswordStrength =
          this.passwordForm.value.password ||
          this.passwordForm.value.confirmation
            ? true
            : false;
        // Password Strength Calculation
        this.progressCount = this.passwordPolicy.filter(
          rule => rule.validated
        ).length;

        this.progressCount === this.passwordPolicy.length
          ? (this.passwordStrength = {
              color: '#37D187',
              label: 'USER_PASSWORD.PASSWORD_STRENGTH.STRONG',
              value: this.progressCount / this.passwordPolicy.length
            })
          : this.progressCount >= 3
          ? (this.passwordStrength = {
              color: '#EFDA20',
              label: 'USER_PASSWORD.PASSWORD_STRENGTH.GOOD',
              value: this.progressCount / this.passwordPolicy.length
            })
          : this.progressCount === 2
          ? (this.passwordStrength = {
              color: '#FABA8C',
              label: 'USER_PASSWORD.PASSWORD_STRENGTH.MEDIUM',
              value: this.progressCount / this.passwordPolicy.length
            })
          : this.progressCount <= 1
          ? (this.passwordStrength = {
              color: '#FF8383',
              label:
                this.progressCount === 1
                  ? 'USER_PASSWORD.PASSWORD_STRENGTH.WEAK'
                  : '',
              value: this.progressCount / this.passwordPolicy.length
            })
          : (this.passwordStrength = { color: null, label: null, value: 0 });

        // Strength bar color fixing
        if (this.progressCount <= 1) {
          this.progressingColor = '#FF8383';
        }
        if (this.progressCount === 2) {
          this.progressingColor = '#FABA8C';
        }
        if (this.progressCount >= 3) {
          this.progressingColor = '#EFDA20';
        }
        if (this.progressCount === this.passwordPolicy.length) {
          this.progressingColor = '#37D187';
        }

        this.valid = this.requireCurrentPassword
          ? this.currentPasswordFieldIsValid &&
            this.passwordPolicy.every(rule => rule.validated) &&
            this.passwordsIdentical.validated
          : this.passwordPolicy.every(rule => rule.validated) &&
            this.passwordsIdentical.validated;

        if (this.formIsValid !== this.valid) {
          this.passwordForm.patchValue({
            passwordFormValid: this.valid
          });
        }

        if (this.showPasswordStrength) {
          setTimeout(() => {
            // Getting Ion progress bar
            const strengthBar = document.querySelector('ion-progress-bar');
            const strengthBarRoot = strengthBar && strengthBar.shadowRoot;
            const progress = strengthBarRoot.querySelector('.progress');
            const bufferProgress = strengthBarRoot.querySelector(
              '.progress-buffer-bar'
            );

            // Styling Ion progress bar and buffer bar
            const progressStyle = document.createElement('style');
            const bufferStyle = document.createElement('style');
            // z-index setting for ios app
            progressStyle.textContent = `.progress { background: ${this.progressingColor} !important; z-index: 0 !important; }`;
            bufferStyle.textContent = `.progress-buffer-bar { background: ${this.progressingColor} !important; opacity: 0.3; z-index: -1 !important; }`;

            // Appending style to Element
            if (progress) {
              progress.append(progressStyle);
            }
            if (bufferProgress) {
              bufferProgress.append(bufferStyle);
            }
          }, 100);
        }
      });
  }

  resetValidatedRule(): void {
    if (this.passwordPolicy) {
      this.passwordPolicy.map(rule => (rule.validated = void 0));
    }
  }

  ngOnDestroy(): void {
    this.alive = false;
    this.resetValidatedRule();
  }

  onSubmit(): void {
    if (this.valid) {
      this.password.emit({
        iAuth:
          this.passwordResetPolicy || this.passwordPolicy ? this.iAuth : void 0,
        newPassword: this.passwordForm.value.password,
        currentPassword: this.passwordForm.value.currentPassword,
        confirmPassword: this.passwordForm.value.confirmation
      });
    }
  }
  handlekeyUp(e) {
    if (
      this.passwordForm.value.password.length != 0 &&
      this.passwordForm.value.currentPassword != 0 &&
      this.passwordForm.value.confirm_password != 0
    ) {
      if (e.keyCode === 13) {
        this.onSubmit();
      }
    }
  }
}
